/**
 * Medications & Safety (Screen F) — a LIVE medication list (REAL, silver) + MIXED safety, each badged
 * from its OWN provenance via ProvenanceBadge (never hand-tagged). A reusable ParticipantPicker selects a
 * member (defaulting to the meds-richest member via the exemplar endpoint):
 *   1. Med list — REAL orders from silver_lakebase.stg_medication_request (bridged via member_xref):
 *      status, intent, authored date, days supply, dose. The coded drug NAME is not carried by the current
 *      silver extract, so the Medication column renders an explicit field-level DEFERRED lane ("Awaiting
 *      feed (RxNorm)", driven by drug_name_status, never blank/faked) — the same field-level honesty as the
 *      Diagnostic Reports result-value lane — so a list of REAL orders never reads as fully-REAL while its
 *      clinically-primary field is unknown. The ROW stays REAL (the order is real); only the name field is
 *      DEFERRED. An empty member is a REAL-empty ("no medications on record"); when
 *      the silver feed is absent the SAME code path renders the DEFERRED state.
 *   2. Order activity — an honest all-status medication ORDER COUNT (REAL from silver), explicitly NOT a
 *      polypharmacy determination. Because every drug name is null in the current silver extract, this is
 *      NOT a reconcilable med list, so polypharmacy / Beers / interaction / adherence determinations are
 *      suppressed (shown as a single deferred note naming the pending coded-RxNorm feed) rather than
 *      rendered as empty/mock cards beside the list, which would invite reconciliation.
 * Adherence / PDC / Beers / interactions are deliberately NOT computed (deferred — never faked). Medication
 * rows are never console.logged; each surface isolates its own loading/error.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  SkyBadge,
  SkyCard,
  SkyDataGrid,
  SkyText,
  SkyLoader,
  SkyMessageBar,
  type SkyBadgeColor,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import type { Medication, MedicationSafety } from "@shared/contracts";
import { usePage } from "../lib/usePage";
import { medicationListSchema, medicationSafetySchema } from "../lib/schemas/member";
import { useExemplarMember } from "../lib/useExemplarMember";
import { ParticipantPicker } from "../components/ParticipantPicker";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { PageError } from "../components/PageError";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

/** FHIR MedicationRequest.status -> semantic badge color (status by color + LABEL, never amber-brand). */
export function medStatusColor(status: string): SkyBadgeColor {
  const s = status.toLowerCase();
  if (s === "active") return "informative";
  if (s === "completed") return "success";
  if (s === "on-hold") return "warning";
  if (s === "stopped" || s === "cancelled" || s === "entered-in-error") return "danger";
  return "subtle"; // unknown / draft
}

const medColumns: SkyDataGridColumn<Medication>[] = [
  {
    key: "medication_display",
    header: "Medication",
    width: "30%",
    // L1 field-level honesty: the drug NAME is the clinically-primary field but is null on every row in the
    // current silver extract. Rather than a quiet "Not recorded" (which reads as a real-but-absent value
    // beside a REAL/green row), render an explicit DEFERRED lane — mirroring the Diagnostic Reports
    // result-value treatment — so the list never presents as fully-REAL while its primary field is unknown.
    // The order row stays REAL; only this field is marked DEFERRED. Never blank, never invented.
    renderCell: (_v, row) =>
      row.medication_display ?? (
        <SkyBadge color="subtle" appearance="tint" size="small">
          {row.drug_name_status === "deferred" ? "Awaiting feed (RxNorm)" : "Not recorded"}
        </SkyBadge>
      ),
  },
  {
    key: "status",
    header: "Status",
    width: "16%",
    renderCell: (_v, row) =>
      row.status ? (
        <SkyBadge color={medStatusColor(row.status)} appearance="tint" size="small">
          {row.status}
        </SkyBadge>
      ) : (
        "Not recorded"
      ),
  },
  { key: "intent", header: "Intent", width: "14%" },
  { key: "authored_on", header: "Authored", width: "16%" },
  {
    key: "dose_quantity",
    header: "Dose",
    width: "12%",
    align: "right",
    renderCell: (_v, row) =>
      row.dose_quantity != null
        ? `${row.dose_quantity}${row.dose_unit ? ` ${row.dose_unit}` : ""}`
        : "Not recorded",
  },
  {
    key: "days_supply",
    header: "Days supply",
    width: "12%",
    align: "right",
    renderCell: (_v, row) => (row.days_supply != null ? String(row.days_supply) : "Not recorded"),
  },
];

function MedListSection({ memberKey }: { memberKey: string }) {
  const { data, meta, loading, error, retry } = usePage<Medication[]>(
    `/api/v1/participants/${memberKey}/medications`,
    medicationListSchema,
  );
  const count = data?.length ?? 0;
  return (
    <SkyCard
      header={{
        title: "Medication orders",
        description: "Medication orders (all statuses) from the silver MedicationRequest extract",
        action: <ProvenanceBadge meta={meta?.provenance} />,
      }}
    >
      {loading && <SkyLoader size="small" label="Loading medications" />}
      {error && <PageError title="Could not load medications" message={error} onRetry={retry} />}
      {!loading && !error && (
        <div className="flex flex-col gap-3">
          <SkyText size={200}>
            {count} medication order(s) on record. The coded drug name and drug class await the
            coded-medication feed; order status, dates and dosing are real.
          </SkyText>
          <SkyDataGrid<Medication>
            data={data ?? []}
            columns={medColumns}
            getRowId={(r) => r.id}
            emptyMessage="No medications on record"
          />
        </div>
      )}
    </SkyCard>
  );
}

function SafetySection({ memberKey }: { memberKey: string }) {
  const { data, loading, error, retry } = usePage<MedicationSafety>(
    `/api/v1/participants/${memberKey}/medications/safety`,
    medicationSafetySchema,
  );
  return (
    <SkyCard
      header={{
        title: "Medication order activity (drug identity pending)",
        description:
          "Order counts only. This is not a reconcilable medication list: every drug name is null in the current silver extract, so no polypharmacy, Beers, interaction or adherence determination is made.",
        action: <ProvenanceBadge meta={data?.polypharmacy.provenance} />,
      }}
    >
      {loading && <SkyLoader size="small" label="Loading order activity" />}
      {error && <PageError title="Could not load order activity" message={error} onRetry={retry} />}
      {!loading && !error && (
        <div className="flex flex-col gap-4">
          {/* Honest all-status ORDER COUNT, explicitly NOT a polypharmacy determination (no status
              filter, no distinct-drug dedup). The bold-number polypharmacy framing is intentionally gone. */}
          <div className="flex flex-col">
            <SkyText size={400} weight="semibold">
              Medication order count
            </SkyText>
            <SkyText size={600} weight="bold" font="numeric">
              {data ? String(data.polypharmacy.data.count) : "Awaiting feed"}
            </SkyText>
            <SkyText size={200} color="muted">
              All-status order count, not a polypharmacy determination. Includes completed, stopped and
              cancelled orders plus duplicate reorders, from the silver MedicationRequest extract.
            </SkyText>
          </div>
          {/* Polypharmacy / Beers / interaction / adherence screening is suppressed (not rendered as
              empty/mock cards) and stays deferred behind the named pending feed, so the surface cannot be
              relied on for reconciliation or read as a confirmed clean screen. */}
          <SkyMessageBar
            intent="info"
            title="Safety screening deferred (coded RxNorm feed)"
            message="Polypharmacy, Beers-criteria, drug-interaction and adherence determinations require coded drug identity (RxNorm), which the current silver extract does not carry. No determination is shown rather than a placeholder that could read as a clean screen."
          />
        </div>
      )}
    </SkyCard>
  );
}

export function Medications() {
  // Manual pick wins; else a ?member= deep-link (from the Member 360 "open in Medications" link); else
  // the meds-richest REAL member (the silver MedicationRequest extract is live, so the medications
  // exemplar defaults the picker to a member who actually has orders). The ParticipantPicker still lets
  // the user change member.
  const [picked, setPicked] = useState<string | null>(null);
  const [params] = useSearchParams();
  const memberParam = params.get("member");
  const validParam = memberParam && /^-?\d+$/.test(memberParam) ? memberParam : null;
  const { key: defaultKey, error: exemplarError } = useExemplarMember("medications");
  const key = picked ?? validParam ?? defaultKey;

  return (
    <>
      <PageHeader
        crumbs={[{ key: "home", label: "skyCare" }, { key: "medications", label: "Medications" }]}
        title="Medications & Safety"
        description="Medication order activity read live from the silver MedicationRequest extract (status, dates and dosing are real; the coded drug name and drug class await the coded-medication feed). Because every drug name is null in the current extract this is not a reconcilable medication list: only an all-status order count is shown, and polypharmacy, Beers, interaction and adherence determinations are deferred until the coded-RxNorm feed lands. An empty list means 'no medications on record', never a feed gap."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        <div className="w-80">
          <ParticipantPicker value={key ?? undefined} onSelect={setPicked} />
        </div>
        {/* The default-member lookup failed and the user has not picked one yet: surface an honest bar
            (the picker above is the way out) instead of a silent blank screen. */}
        {exemplarError && !key && (
          <SkyMessageBar
            intent="warning"
            title="Could not load a default member"
            message="Could not load a default member - search above to choose a participant."
          />
        )}
        {key && <MedListSection memberKey={key} />}
        {key && <SafetySection memberKey={key} />}
      </section>
      <PageFooter />
    </>
  );
}
